import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { eventTaskGeneratorService } from './event-task-generator.service';

// Configuration des collections pour chaque type d'événement
const EVENT_COLLECTIONS = {
  wedding: {
    main: 'weddings',
    tasks: 'tasks',
    guests: 'guests',
    vendors: 'vendors',
    expenses: 'expenses',
    notifications: 'notifications',
    checklist: 'checklist',
    timeline: 'timeline_events'
  },
  birthday: {
    main: 'birthdays',
    tasks: 'birthday_tasks',
    guests: 'birthday_guests',
    vendors: 'birthday_vendors',
    expenses: 'birthday_expenses',
    notifications: 'birthday_notifications',
    checklist: 'birthday_checklist',
    timeline: 'birthday_timeline'
  },
  engagement: {
    main: 'engagements',
    tasks: 'engagement_tasks',
    guests: 'engagement_guests',
    vendors: 'engagement_vendors',
    expenses: 'engagement_expenses',
    notifications: 'engagement_notifications',
    checklist: 'engagement_checklist',
    timeline: 'engagement_timeline'
  },
  babyshower: {
    main: 'babyshowers',
    tasks: 'babyshower_tasks',
    guests: 'babyshower_guests',
    vendors: 'babyshower_vendors',
    expenses: 'babyshower_expenses',
    notifications: 'babyshower_notifications',
    checklist: 'babyshower_checklist',
    timeline: 'babyshower_timeline'
  },
  baptism: {
    main: 'baptisms',
    tasks: 'baptism_tasks',
    guests: 'baptism_guests',
    vendors: 'baptism_vendors',
    expenses: 'baptism_expenses',
    notifications: 'baptism_notifications',
    checklist: 'baptism_checklist',
    timeline: 'baptism_timeline'
  },
  other: {
    main: 'other_events',
    tasks: 'other_tasks',
    guests: 'other_guests',
    vendors: 'other_vendors',
    expenses: 'other_expenses',
    notifications: 'other_notifications',
    checklist: 'other_checklist',
    timeline: 'other_timeline'
  }
};

export interface GenericEventDetails {
  date: Date;
  location?: {
    city?: string;
    venue?: string;
    address?: string;
    country?: string;
  };
  budget?: {
    total: number;
    categories: {
      [key: string]: number;
    };
    estimatedGuestCount?: number;
  };
  style?: {
    colors?: string[];
    theme?: string;
  };
  type?: string;
  language?: string;
}

export interface GenericEvent {
  id?: string;
  userId: string;
  name: string;
  type: string;
  eventDetails: GenericEventDetails;
  partners?: {
    partner1: {
      firstName: string;
      lastName?: string;
      email?: string;
    };
    partner2: {
      firstName: string;
      lastName?: string;
      email?: string;
    };
  };
  collaborators: string[];
  createdAt: Date;
  updatedAt: Date;
}

export class GenericEventService {
  private getCollections(eventType: string) {
    const normalizedType = eventType.toLowerCase();
    return EVENT_COLLECTIONS[normalizedType as keyof typeof EVENT_COLLECTIONS] || EVENT_COLLECTIONS.other;
  }

  // Créer un événement générique
  async createEvent(userId: string, eventType: string, data: Partial<GenericEvent>, userLanguage: string = 'fr'): Promise<string> {
    const collections = this.getCollections(eventType);

    try {
      const eventRef = doc(collection(db, collections.main));

      if (!data.eventDetails?.date) {
        throw new Error('La date de l\'événement est requise');
      }

      const eventDate = data.eventDetails.date instanceof Date
        ? data.eventDetails.date
        : new Date(data.eventDetails.date);

      const event: GenericEvent = {
        id: eventRef.id,
        userId,
        name: data.name || 'Nouvel événement',
        type: eventType,
        eventDetails: {
          date: eventDate,
          location: data.eventDetails.location || {
            venue: '',
            address: '',
            city: ''
          },
          type: eventType,
          language: data.eventDetails.language || 'fr'
        },
        collaborators: [userId],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const eventToSave = {
        ...event,
        eventDetails: {
          ...event.eventDetails,
          date: Timestamp.fromDate(eventDate)
        },
        createdAt: Timestamp.fromDate(event.createdAt),
        updatedAt: Timestamp.fromDate(event.updatedAt)
      };

      await setDoc(eventRef, eventToSave);

      // Extraire le pays des données de localisation
      const country = data.eventDetails?.location?.country;
      await this.initializeEventData(eventRef.id, eventType, eventDate, country, userLanguage);

      return eventRef.id;
    } catch (error) {
      console.error('❌ [GenericEventService] Error in createEvent:', error);
      console.error('❌ [GenericEventService] Error stack:', error instanceof Error ? error.stack : 'No stack');
      throw error;
    }
  }

  // Récupérer un événement par son ID
  async getEvent(eventId: string, eventType: string): Promise<GenericEvent | null> {
    const collections = this.getCollections(eventType);

    try {
      const eventRef = doc(db, collections.main, eventId);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.warn(`[DEBUG] Document ${eventId} does not exist`);
        return null;
      }

      const eventData = eventDoc.data();

      let eventName = eventData['name'];

      // Polyfill pour les mariages legacy qui n'ont pas de champ 'name'
      if (!eventName && (eventType === 'wedding' || !eventType) && eventData['partners']) {
        const p1 = eventData['partners'].partner1?.firstName;
        const p2 = eventData['partners'].partner2?.firstName;
        if (p1 && p2) {
          eventName = `Mariage de ${p1} & ${p2}`;
        } else if (p1) {
          eventName = `Mariage de ${p1}`;
        }
      }

      return {
        id: eventDoc.id,
        userId: eventData['userId'],
        name: eventName || 'Événement',
        type: eventData['type'] || eventType,
        eventDetails: {
          ...eventData['eventDetails'],
          date: eventData['eventDetails']?.date?.toDate() || new Date()
        },
        partners: eventData['partners'] || undefined,
        collaborators: eventData['collaborators'] || [],
        createdAt: eventData['createdAt']?.toDate() || new Date(),
        updatedAt: eventData['updatedAt']?.toDate() || new Date()
      };
    } catch (error: any) {
      // Ignorer les erreurs de permission qui peuvent survenir lors de la recherche
      // d'un événement dans les différentes collections (boucle dans UnifiedEventContext)
      if (error?.code === 'permission-denied') {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.warn(`[GenericEventService] Permission denied for ${eventType}/${eventId} (expected if guessing type)`);
        return null;
      }
      console.error('[GenericEventService] Error getting event:', error);
      return null;
    }
  }

  // Mettre à jour un événement générique
  async updateEvent(eventId: string, eventType: string, data: Partial<GenericEvent>): Promise<void> {
    const collections = this.getCollections(eventType);

    try {
      const eventRef = doc(db, collections.main, eventId);

      // Préparer les données à mettre à jour
      const updateData: any = {
        ...data,
        updatedAt: serverTimestamp()
      };

      // Si la date est fournie dans eventDetails, la convertir en Timestamp
      if (data.eventDetails?.date) {
        const eventDate = data.eventDetails.date instanceof Date
          ? data.eventDetails.date
          : new Date(data.eventDetails.date);

        updateData.eventDetails = {
          ...data.eventDetails,
          date: Timestamp.fromDate(eventDate)
        };
      }

      await updateDoc(eventRef, updateData);
    } catch (error) {
      console.error('❌ [GenericEventService] Error updating event:', error);
      throw error;
    }
  }

  // Récupérer tous les événements d'un utilisateur pour un type donné
  async getUserEvents(userId: string, eventType: string): Promise<GenericEvent[]> {
    const collections = this.getCollections(eventType);

    try {
      const eventsRef = collection(db, collections.main);
      const q = query(eventsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);

      const results = querySnapshot.docs
        .map((doc, index) => {
        const data = doc.data();

        if (data['eventDetails']) {}

        // Vérifier que la structure de données est valide
        if (!data['eventDetails']) {
          console.warn(`[DEBUG] ⚠️ Document ${doc.id} has no eventDetails, skipping`);
          return null;
        }

        if (!data['eventDetails'].date) {
          console.warn(`[DEBUG] ⚠️ Document ${doc.id} has eventDetails but no date, skipping`);
          return null;
        }

        try {
          const event = {
            ...data,
            id: doc.id,
            eventDetails: {
              ...data['eventDetails'],
              date: data['eventDetails'].date.toDate()
            },
            partners: data['partners'] || undefined,
            createdAt: data['createdAt']?.toDate() || new Date(),
            updatedAt: data['updatedAt']?.toDate() || new Date()
          } as GenericEvent;

          return event;
        } catch (dateError) {
          console.warn(`[DEBUG] ❌ Error converting date for document ${doc.id}:`, dateError);
          return null;
        }
      })
        .filter((event): event is GenericEvent => event !== null);

      // Vérifier et générer les tâches pour les événements qui n'en ont pas
      for (const event of results) {
        await this.ensureEventHasTasks(event);
      }

      return results;
    } catch (error) {
      console.error(`[DEBUG] ❌ Erreur lors de la récupération des événements ${eventType}:`, error);
      return [];
    }
  }

  // Vérifier si un événement a des tâches et les générer si nécessaire
  private async ensureEventHasTasks(event: GenericEvent): Promise<void> {
    if (!event.id) {
      console.warn(`⚠️ [GenericEventService] Event has no ID, skipping task generation`);
      return;
    }

    try {
      // Utiliser directement generateTasksIfNeeded qui fait déjà la vérification
      const { eventTaskGeneratorService } = await import('./event-task-generator.service');

      // Extraire le pays depuis les détails de l'événement
      const country = event.eventDetails?.location?.country;

      // La méthode generateTasksIfNeeded vérifie automatiquement l'existence
      await eventTaskGeneratorService.generateTasksIfNeeded(event.id, event.type, event.eventDetails.date, country);
    } catch (error) {
      console.error(`❌ [GenericEventService] Error ensuring tasks for event ${event.id}:`, error);
      // Ne pas faire échouer le chargement de l'événement si la génération de tâches échoue
    }
  }

  // Initialiser les données pour un nouvel événement
  async initializeEventData(eventId: string, eventType: string, eventDate: Date, country?: string, userLanguage: string = 'fr'): Promise<void> {
    try {
      // Vérifier si le service existe
      if (!eventTaskGeneratorService) {
        console.error(`❌ [GenericEventService] eventTaskGeneratorService is null or undefined`);
        return;
      }

      // Vérifier si la méthode existe
      if (typeof eventTaskGeneratorService.generateTasksForEvent !== 'function') {
        console.error(`❌ [GenericEventService] generateTasksForEvent method not found`);
        return;
      }

      await eventTaskGeneratorService.generateTasksIfNeeded(eventId, eventType, eventDate, country, userLanguage);
      const { genericTasksService } = await import('./generic-tasks.service');
      const createdTasks = await genericTasksService.getEventTasks(eventId, eventType);

      if (createdTasks.length === 0) {
        console.warn(`⚠️ [GenericEventService] No tasks were created - this might indicate an issue with task generation`);
      } else {}
    } catch (error) {
      console.error(`❌ [GenericEventService] Error initializing data for event ${eventId}:`, error);
      console.error(`❌ [GenericEventService] Error details:`, {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown',
        eventId,
        eventType,
        eventDate,
        country
      });

      // Essayer de diagnostiquer le problème
      try {
        // Vérifier si les templates existent
        const { getTaskTemplatesForEventType } = await import('../../constants/event-task-templates');
        const templates = getTaskTemplatesForEventType(eventType);

        // Vérifier si les tâches légales existent
        if (country) {
          const { getLegalTasksForCountryAndEvent } = await import('../../constants/legal-task-templates');
          const legalTasks = getLegalTasksForCountryAndEvent(country, eventType);
        }
      } catch (diagError) {
        console.error(`❌ [GenericEventService] Error during diagnosis:`, diagError);
      }

      // Ne pas faire échouer la création de l'événement si la génération de tâches échoue
    }
  }

  // Supprimer un événement générique
  async deleteEvent(eventId: string, eventType: string): Promise<void> {
    const collections = this.getCollections(eventType);

    try {
      const eventRef = doc(db, collections.main, eventId);

      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        console.error(`❌ [GenericEventService] Document ${eventId} n'existe pas`);
        throw new Error(`L'événement ${eventId} n'existe pas`);
      }

      await deleteDoc(eventRef);
    } catch (error) {
      console.error('❌ [GenericEventService] Erreur lors de la suppression:', error);
      throw error;
    }
  }

  // Mettre à jour le budget d'un événement générique
  async updateEventBudget(eventId: string, totalBudget: number, estimatedGuestCount?: number): Promise<any> {
    try {
      // Rechercher l'événement dans toutes les collections possibles
      const allCollections = Object.values(EVENT_COLLECTIONS).map(config => config.main);
      let eventRef = null;
      let eventDoc = null;

      for (const collectionName of allCollections) {
        const testRef = doc(db, collectionName, eventId);
        const testDoc = await getDoc(testRef);
        if (testDoc.exists()) {
          eventRef = testRef;
          eventDoc = testDoc;
          break;
        }
      }

      if (!eventRef || !eventDoc) {
        console.error(`[DEBUG] Événement non trouvé lors de la mise à jour du budget`);
        throw new Error('Événement non trouvé');
      }

      // Mettre à jour le budget
      const updateData: any = {
        'eventDetails.budget.total': totalBudget,
        updatedAt: serverTimestamp()
      };

      if (estimatedGuestCount !== undefined) {
        updateData['eventDetails.budget.estimatedGuestCount'] = estimatedGuestCount;
      }

      await updateDoc(eventRef, updateData);

      return { success: true, eventId, totalBudget, estimatedGuestCount };
    } catch (error) {
      console.error(`[DEBUG] Erreur lors de la mise à jour du budget:`, error);
      throw error;
    }
  }

  // Obtenir les noms des collections pour un type d'événement
  getCollectionNames(eventType: string) {
    return this.getCollections(eventType);
  }
}

export const genericEventService = new GenericEventService(); 